import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowDropDown,
  AutoAwesomeOutlined,
  DeleteOutline,
  DescriptionOutlined,
  EditOutlined,
  GridViewRounded,
  MoreVert,
  PostAddOutlined,
  Search,
  SlideshowOutlined,
  SortRounded,
  UploadFileOutlined,
  ViewListRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth as useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { useUsage } from '../context/UsageContext';
import { hasReachedLimit } from '../services/usageService';
import { formatDate } from '../utils/dateUtils';
import { analyzeTemplateQuality } from '../utils/templateQualityScore';
import { templateEditorPath } from '../utils/navigation';
import TemplateAutoGenerator from './TemplateAutoGenerator';
import { ConfirmDialog, EmptyState, IconTile, PageContainer, PageHeader, SectionCard } from './pagekit';
import CreateWithInkyDialog from './templates/CreateWithInkyDialog';
import { buildTemplateSketch, editorPathFor } from './templates/templateHelpers';
import { isPresentationTemplateType } from './documents/documentMeta';

const VIEW_STORAGE_KEY = 'quill.templates.view.v1';
const SORT_OPTIONS = [
  { value: 'updated', label: 'Last updated' },
  { value: 'name', label: 'Name' },
  { value: 'quality', label: 'Quality score' },
];
const CARD_GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 236px), 1fr))', gap: 2 };

/**
 * Read the saved grid/list preference.
 * @returns {'grid'|'list'} View.
 */
function readStoredView() {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

/**
 * Quality bar + label for a document template.
 * @param {object} props - Props.
 * @param {object|undefined} props.quality - analyzeTemplateQuality result.
 * @returns {JSX.Element} Badge.
 */
function QualityBadge({ quality }) {
  if (!quality) {
    return <Chip size="small" label="Not scored" sx={{ alignSelf: 'flex-start', borderRadius: '6px' }} />;
  }
  const tone = { success: 'var(--q-success)', warning: 'var(--q-warning)', error: 'var(--q-danger)' }[quality.color];
  const text = { success: 'var(--q-success-text)', warning: 'var(--q-warning-text)', error: 'var(--q-danger-text)' }[quality.color];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <LinearProgress
        variant="determinate"
        value={quality.score}
        aria-label={`Quality ${quality.score} of 100`}
        sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'var(--q-border-subtle)', '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 2 } }}
      />
      <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {quality.label} · {quality.score}
      </Typography>
    </Box>
  );
}

/**
 * Abstract page thumbnail built from the template's structure.
 * @param {object} props - Props.
 * @param {object} props.template - Template.
 * @returns {JSX.Element} Thumbnail.
 */
function TemplateThumbnail({ template }) {
  const deck = isPresentationTemplateType(template.templateType);
  const rows = useMemo(() => (deck ? [] : buildTemplateSketch(template.content)), [deck, template.content]);
  if (deck) {
    return (
      <Box aria-hidden sx={{ height: 132, bgcolor: 'var(--q-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 168, height: 94, bgcolor: 'var(--q-surface)', borderRadius: '4px', boxShadow: 'var(--q-shadow-sm)', p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px' }}>
          <Box sx={{ height: 7, width: '60%', borderRadius: 1, bgcolor: 'var(--q-text-2)' }} />
          <Box sx={{ height: 4, width: '40%', borderRadius: 1, bgcolor: 'var(--q-disabled)' }} />
          <Box sx={{ height: 4, width: 24, borderRadius: 1, bgcolor: 'var(--q-accent)', mt: 0.5 }} />
        </Box>
      </Box>
    );
  }
  return (
    <Box aria-hidden sx={{ height: 132, bgcolor: 'var(--q-subtle)', pt: 2, px: 3, display: 'flex' }}>
      <Box sx={{ flex: 1, bgcolor: 'var(--q-surface)', borderRadius: '4px 4px 0 0', boxShadow: 'var(--q-shadow-sm)', px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: '6px', overflow: 'hidden' }}>
        {rows.map((row, index) => {
          if (row.kind === 'field') {
            return <Box key={index} sx={{ height: 14, width: '100%', flex: 'none', borderRadius: 1, bgcolor: 'var(--q-accent-soft)', border: '1px dashed var(--q-accent-muted)' }} />;
          }
          const style = {
            title: { height: 6, bgcolor: 'var(--q-text-2)' },
            heading: { height: 4, bgcolor: 'var(--q-disabled)', mt: 0.5 },
            text: { height: 4, bgcolor: 'var(--q-border)' },
          }[row.kind];
          return <Box key={index} sx={{ ...style, flex: 'none', width: `${row.width}%`, borderRadius: 1 }} />;
        })}
      </Box>
    </Box>
  );
}

/**
 * Grid card for a template.
 * @param {object} props - Props.
 * @param {object} props.template - Template.
 * @param {object|undefined} props.quality - Quality result.
 * @param {function(): void} props.onOpen - Open the editor.
 * @param {function(React.MouseEvent<HTMLElement>): void} [props.onMenu] - Open actions (managers only).
 * @returns {JSX.Element} Card.
 */
function TemplateCard({ template, quality, onOpen, onMenu }) {
  const deck = isPresentationTemplateType(template.templateType);
  const meta = deck
    ? `Created ${formatDate(template.createdAt)}`
    : `${quality?.metrics.promptCount ?? 0} AI field${quality?.metrics.promptCount === 1 ? '' : 's'} · ${formatDate(template.updatedAt || template.createdAt)}`;
  return (
    <Box
      component="article"
      sx={{
        position: 'relative',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover, &:focus-within': { borderColor: 'var(--q-border-hover)', boxShadow: 'var(--q-shadow-md)' },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={`Open ${template.name}`}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'block',
          '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: -2 },
        }}
      >
        <TemplateThumbnail template={template} />
      </Box>
      <Box sx={{ px: 2, pt: 1.75, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.25, borderTop: '1px solid var(--q-border-subtle)', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h3"
              onClick={onOpen}
              sx={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
              title={template.name}
            >
              {template.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {meta}
            </Typography>
          </Box>
          {onMenu && (
            <IconButton
              size="small"
              aria-label={`Actions for ${template.name}`}
              aria-haspopup="menu"
              onClick={onMenu}
              sx={{ mt: -0.5, mr: -0.75, color: 'text.secondary' }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          )}
        </Box>
        {template.description && (
          <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {template.description}
          </Typography>
        )}
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {template.status === 'draft' && <Chip size="small" color="warning" label="Draft, needs review" sx={{ alignSelf: 'flex-start' }} />}
          <QualityBadge quality={quality} />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Dashed "Blank template" tile at the start of the documents grid.
 * @param {object} props - Props.
 * @param {function(): void} props.onClick - Create a blank template.
 * @returns {JSX.Element} Tile.
 */
function BlankTemplateTile({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        cursor: 'pointer',
        minHeight: { xs: 140, sm: 286 },
        border: '1.5px dashed var(--q-border-hover)',
        borderRadius: 'var(--q-radius-card)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        color: 'var(--q-text-2)',
        textAlign: 'center',
        p: 2,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': { borderColor: 'var(--q-accent)', bgcolor: 'var(--q-accent-tint)' },
        '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 2 },
      }}
    >
      <Box sx={{ width: 44, height: 44, borderRadius: 999, bgcolor: 'var(--q-surface)', border: '1px solid var(--q-border)', color: 'var(--q-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AddRounded />
      </Box>
      <Typography sx={{ fontWeight: 600 }}>Blank template</Typography>
      <Typography variant="caption" color="text.secondary">Start from an empty page</Typography>
    </Box>
  );
}

/**
 * Templates page: grouped grid or list of templates with search, type/quality filters, sort,
 * quality badges and creation entry points (blank, PowerPoint, import, Create with Inky).
 * @returns {JSX.Element} Page.
 */
function TemplateList() {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { state: authState, actions: authActions } = useEnterpriseAuth();
  const { refreshUsage } = useUsage();
  const canManage = authActions.canManageTemplates();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);
  const [autoGeneratorOpen, setAutoGeneratorOpen] = useState(false);
  const [inkyOpen, setInkyOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [limitReachedOpen, setLimitReachedOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [view, setView] = useState(readStoredView);

  const qualityById = useMemo(() => {
    const scores = {};
    state.templates.forEach((template) => {
      if (!isPresentationTemplateType(template.templateType)) {
        scores[template.id] = analyzeTemplateQuality(template.content || '');
      }
    });
    return scores;
  }, [state.templates]);

  const counts = useMemo(() => {
    const decks = state.templates.filter((t) => isPresentationTemplateType(t.templateType)).length;
    const needsWork = Object.values(qualityById).filter((q) => q.score < 80).length;
    return { all: state.templates.length, documents: state.templates.length - decks, presentations: decks, needs_work: needsWork };
  }, [state.templates, qualityById]);

  const visibleTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = state.templates.filter((template) => {
      const deck = isPresentationTemplateType(template.templateType);
      if (typeFilter === 'documents' && deck) return false;
      if (typeFilter === 'presentations' && !deck) return false;
      if (typeFilter === 'needs_work' && !(qualityById[template.id]?.score < 80)) return false;
      if (!q) return true;
      return String(template.name || '').toLowerCase().includes(q) || String(template.description || '').toLowerCase().includes(q);
    });
    const time = (t) => new Date(t.updatedAt || t.createdAt || 0).getTime();
    const sorters = {
      updated: (a, b) => time(b) - time(a),
      name: (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }),
      quality: (a, b) => (qualityById[b.id]?.score ?? -1) - (qualityById[a.id]?.score ?? -1),
    };
    return [...filtered].sort(sorters[sortBy]);
  }, [state.templates, searchQuery, typeFilter, sortBy, qualityById]);

  const groups = useMemo(() => {
    const docs = visibleTemplates.filter((t) => !isPresentationTemplateType(t.templateType));
    const decks = visibleTemplates.filter((t) => isPresentationTemplateType(t.templateType));
    return [
      { key: 'documents', title: 'Documents', meta: 'Word', items: docs },
      { key: 'presentations', title: 'Presentations', meta: 'PowerPoint', items: decks },
    ];
  }, [visibleTemplates]);

  const changeView = (next) => {
    if (!next) return;
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Preference only.
    }
  };

  /**
   * Run `fn` unless the plan's template limit is reached (then show the upgrade dialog).
   * @param {function(): void} fn - Creation action.
   * @returns {Promise<void>}
   */
  const withTemplateQuota = useCallback(async (fn) => {
    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'templates')) {
      setLimitReachedOpen(true);
      return;
    }
    fn();
  }, [refreshUsage]);

  const createDocumentTemplate = () => withTemplateQuota(() => navigate(templateEditorPath({ templateId: 'new' })));
  const createPresentationTemplate = () => withTemplateQuota(() => navigate(templateEditorPath({ templateId: 'new', kind: 'powerpoint-rich' })));
  const openAutoGenerator = () => withTemplateQuota(() => setAutoGeneratorOpen(true));
  const openInky = () => withTemplateQuota(() => setInkyOpen(true));

  const handleTemplateCreated = (template) => {
    refreshUsage();
    navigate(editorPathFor(template));
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setSelectedTemplate(null);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    try {
      const result = await actions.deleteTemplate(templateToDelete.id);
      setSnackbar(result.success
        ? { open: true, message: `Template "${templateToDelete.name}" deleted`, severity: 'success' }
        : { open: true, message: result.error || 'Failed to delete template', severity: 'error' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to delete template', severity: 'error' });
    } finally {
      setDeleting(false);
      setTemplateToDelete(null);
    }
  };

  const openMenu = (event, template) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTemplate(template);
  };

  const loading = state.loading || (!state.initialLoadComplete && authState.loading) || !state.initialLoadComplete;
  const hasFilters = searchQuery.trim() !== '' || typeFilter !== 'all';

  const headerActions = canManage ? (
    <>
      <Button
        startIcon={<AutoAwesomeOutlined />}
        onClick={openInky}
        sx={{ bgcolor: 'var(--q-accent-soft)', color: 'var(--q-accent-strong)', '&:hover': { bgcolor: 'var(--q-accent-selected)' } }}
      >
        Create with Inky
      </Button>
      <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={openAutoGenerator}>
        Import a file
      </Button>
      <ButtonGroup variant="contained" aria-label="New template">
        <Button startIcon={<AddRounded />} onClick={createDocumentTemplate}>New template</Button>
        <Button
          size="small"
          aria-label="More template types"
          aria-haspopup="menu"
          onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
          sx={{ px: 0.75, minWidth: 0 }}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
    </>
  ) : null;

  const renderGrid = (group) => (
    <Box sx={CARD_GRID_SX}>
      {canManage && group.key === 'documents' && !hasFilters && <BlankTemplateTile onClick={createDocumentTemplate} />}
      {group.items.map((template) => (
        <TemplateCard
          key={template.id}
          template={template}
          quality={qualityById[template.id]}
          onOpen={() => navigate(editorPathFor(template))}
          onMenu={canManage ? (event) => openMenu(event, template) : undefined}
        />
      ))}
    </Box>
  );

  const renderTable = () => (
    <SectionCard flush>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 640 }} aria-label="Templates">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 2.5 }}>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">AI fields</TableCell>
              <TableCell sx={{ width: 200 }}>Quality</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right" sx={{ pr: 2.5 }}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTemplates.map((template) => {
              const deck = isPresentationTemplateType(template.templateType);
              const quality = qualityById[template.id];
              return (
                <TableRow key={template.id} hover>
                  <TableCell sx={{ pl: 2.5, maxWidth: 360 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <IconTile tone={deck ? 'neutral' : 'accent'} size={30}>
                        {deck ? <SlideshowOutlined /> : <DescriptionOutlined />}
                      </IconTile>
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => navigate(editorPathFor(template))}
                          sx={{ all: 'unset', cursor: 'pointer', fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' }, '&:focus-visible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 2, borderRadius: '4px' } }}
                        >
                          {template.name}
                        </Box>
                        {template.description && (
                          <Typography variant="caption" color="text.secondary" noWrap component="p">{template.description}</Typography>
                        )}
                      </Box>
                      {template.status === 'draft' && <Chip size="small" color="warning" label="Draft" />}
                    </Box>
                  </TableCell>
                  <TableCell>{deck ? 'PowerPoint' : 'Word'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{deck ? '—' : quality?.metrics.promptCount ?? 0}</TableCell>
                  <TableCell><QualityBadge quality={quality} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatDate(template.updatedAt || template.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ pr: 2.5 }}>
                    {canManage && (
                      <IconButton size="small" aria-label={`Actions for ${template.name}`} aria-haspopup="menu" onClick={(e) => openMenu(e, template)}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );

  const filterChips = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'documents', label: 'Documents', count: counts.documents },
    { value: 'presentations', label: 'Presentations', count: counts.presentations },
    { value: 'needs_work', label: 'Needs work', count: counts.needs_work, dot: true },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Templates"
        description="Reusable structures with AI fields that Inky fills from a project's requirements and sessions."
        actions={headerActions}
      />

      {!loading && state.templates.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={typeFilter}
            onChange={(e, next) => next && setTypeFilter(next)}
            aria-label="Filter templates"
            sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { border: '1px solid var(--q-border-strong) !important', borderRadius: 'var(--q-radius-md) !important', m: 0 } }}
          >
            {filterChips.map((chip) => (
              <ToggleButton key={chip.value} value={chip.value} disabled={chip.value !== 'all' && chip.count === 0 && typeFilter !== chip.value}>
                {chip.dot && <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: 'var(--q-warning)', mr: 0.75 }} />}
                {chip.label}
                <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 500 }}>{chip.count}</Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
          <TextField
            placeholder="Search templates"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 220px' }, maxWidth: { md: 280 } }}
            inputProps={{ 'aria-label': 'Search templates' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          <Button
            color="inherit"
            startIcon={<SortRounded />}
            onClick={(e) => setSortAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-label={`Sort by ${SORT_OPTIONS.find((o) => o.value === sortBy)?.label}`}
          >
            {SORT_OPTIONS.find((o) => o.value === sortBy)?.label}
          </Button>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, next) => changeView(next)} aria-label="View">
            <ToggleButton value="grid" aria-label="Grid view"><GridViewRounded fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="List view"><ViewListRounded fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {loading ? (
        <Box sx={CARD_GRID_SX} role="progressbar" aria-label="Loading templates" aria-busy="true">
          {[0, 1, 2, 3].map((key) => (
            <Box key={key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', overflow: 'hidden', bgcolor: 'background.paper' }}>
              <Skeleton variant="rectangular" height={132} />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="45%" />
                <Skeleton variant="rounded" height={6} sx={{ mt: 1.5 }} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : state.templates.length === 0 ? (
        <SectionCard>
          <EmptyState
            inky
            title="No templates yet"
            description={canManage
              ? 'A template is the fixed structure of a document plus AI fields Inky fills from your projects. Describe one to Inky, import a sample, or start blank.'
              : 'Your workspace has no templates yet. Ask an admin or template creator to add one.'}
            actions={canManage && (
              <>
                <Button variant="contained" startIcon={<AutoAwesomeOutlined />} onClick={openInky}>Create with Inky</Button>
                <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={openAutoGenerator}>Import a file</Button>
                <Button onClick={createDocumentTemplate}>Start blank</Button>
              </>
            )}
          />
        </SectionCard>
      ) : visibleTemplates.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<Search />}
            title="No templates match"
            description="Try a different search or filter."
            actions={(
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setTypeFilter('all');
                }}
              >
                Clear filters
              </Button>
            )}
          />
        </SectionCard>
      ) : view === 'list' ? (
        renderTable()
      ) : (
        groups.filter((group) => group.items.length > 0 || (group.key === 'documents' && canManage && !hasFilters)).map((group) => (
          <Box component="section" key={group.key} aria-label={group.title} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
              <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>{group.title}</Typography>
              <Typography variant="body2" color="text.secondary">{group.items.length} · {group.meta}</Typography>
            </Box>
            {renderGrid(group)}
          </Box>
        ))
      )}

      {/* Per-template actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            const template = selectedTemplate;
            closeMenu();
            if (template) navigate(editorPathFor(template));
          }}
        >
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const template = selectedTemplate;
            closeMenu();
            if (template) navigate(`/generate?templateId=${encodeURIComponent(template.id)}`);
          }}
        >
          <ListItemIcon><PostAddOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Generate a document</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const template = selectedTemplate;
            closeMenu();
            if (template) setTemplateToDelete(template);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutline fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* New template types */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { setCreateMenuAnchor(null); createDocumentTemplate(); }}>
          <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="Document template" secondary="Word document with AI fields" />
        </MenuItem>
        <MenuItem onClick={() => { setCreateMenuAnchor(null); createPresentationTemplate(); }}>
          <ListItemIcon><SlideshowOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="PowerPoint template" secondary="Slides with variables" />
        </MenuItem>
      </Menu>

      {/* Sort */}
      <Menu anchorEl={sortAnchor} open={Boolean(sortAnchor)} onClose={() => setSortAnchor(null)}>
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === sortBy}
            onClick={() => {
              setSortBy(option.value);
              setSortAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <TemplateAutoGenerator
        open={autoGeneratorOpen}
        onClose={() => setAutoGeneratorOpen(false)}
        onTemplateCreated={handleTemplateCreated}
      />

      <CreateWithInkyDialog
        open={inkyOpen}
        onClose={() => setInkyOpen(false)}
        onCreated={(template) => {
          setInkyOpen(false);
          handleTemplateCreated(template);
        }}
      />

      <Dialog open={limitReachedOpen} onClose={() => setLimitReachedOpen(false)}>
        <DialogTitle>Template limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've reached your template limit. Upgrade your plan to create more templates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReachedOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLimitReachedOpen(false);
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(templateToDelete)}
        title="Delete template?"
        message={`"${templateToDelete?.name || ''}" will be permanently deleted. This cannot be undone.`}
        busy={deleting}
        onConfirm={handleConfirmDelete}
        onClose={() => setTemplateToDelete(null)}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}

/** Visually hidden but readable by screen readers. */
const visuallyHidden = {
  position: 'absolute',
  // Strings: in sx a bare 1 means 100%, not 1px.
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export default TemplateList;
